package com.example.crashbit

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.toArgb
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlin.random.Random

@Serializable
data class SpaceBody(
    val id: Long = Random.nextLong(),
    val name: String = "Unnamed-Body",
    @Serializable(with = OffsetSerializer::class)
    var position: Offset,
    @Serializable(with = OffsetSerializer::class)
    var velocity: Offset,
    var mass: Float,
    var radius: Float,
    @Serializable(with = ColorSerializer::class)
    var color: Color,
    var isStar: Boolean = false,
    var isBlackHole: Boolean = false,
    var hasRings: Boolean = false,
    var temperature: Float = 250f,
    var rotation: Float = 0f,
    var rotationSpeed: Float = 0f,
    var lifeProgress: Float = 0f,
    var alienProgress: Float = 0f,
    var hasLife: Boolean = false,
    var hasAliens: Boolean = false,
    var jordyPopulation: Long = 0L,
    var parentIds: List<Long> = emptyList(),
    var minObservedDist: Float = Float.MAX_VALUE,
    var maxObservedDist: Float = 0f,
    var composition: Map<String, Float> = emptyMap(),
    var closePassCount: Map<Long, Int> = emptyMap(),
    var currentlyOverlapping: Set<Long> = emptySet(),
    var overlapFrameCount: Map<Long, Int> = emptyMap(),
    @kotlinx.serialization.Transient var renderPosition: Offset = position,
    @kotlinx.serialization.Transient var renderVelocity: Offset = velocity,
    @kotlinx.serialization.Transient var lastWarpExitId: Long? = null,
    @kotlinx.serialization.Transient var bufferIndex: Int = -1,
    @kotlinx.serialization.Transient var previousPosition: Offset = position,
    @kotlinx.serialization.Transient var previousRotation: Float = rotation
)

data class VisualEffect(
    val type: EffectType,
    val position: Offset,
    val startTime: Long,
    val durationMs: Long,
    val intensity: Float = 1.0f,
    val color: Color = Color.White,
    val parentId: Long? = null
)

enum class EffectType {
    GRAVITATIONAL_WAVE,
    OFFSCREEN_MERGER,
    STAR_COLLAPSE,
    BLACK_HOLE_COLLAPSE,
    ACCRETION_BURST,
    SOLAR_FLARE,
    SUPERNOVA_SHOCKWAVE
}

object OffsetSerializer : KSerializer<Offset> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("Offset", PrimitiveKind.STRING)
    override fun serialize(encoder: Encoder, value: Offset) = encoder.encodeString("${value.x},${value.y}")
    override fun deserialize(decoder: Decoder): Offset {
        val parts = decoder.decodeString().split(",")
        return Offset(parts[0].toFloat(), parts[1].toFloat())
    }
}

object ColorSerializer : KSerializer<Color> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("Color", PrimitiveKind.INT)
    override fun serialize(encoder: Encoder, value: Color) = encoder.encodeInt(value.toArgb())
    override fun deserialize(decoder: Decoder): Color = Color(decoder.decodeInt())
}
